import { FieldValues, UseFormGetValues, UseFormWatch } from 'react-hook-form'
import { DataItem } from 'scrivito'

import { getAttributeDisplayValue } from '../displayValue'
import { getCustomFieldKey } from './getCustomFieldKey'
import { DataItemAttributesFlatMap, DataItemMap, FieldMap } from './types'
import { getDataItemEnumSelectArray } from '../enum/getDataItemEnumSelectArray'
import { getReferenceOptionsFunction, getReferenceDataItem } from '../reference'
import { getSchemaAttributes } from '../schema'
import { AttributeType, SchemaAttribute } from '@/Data/typesV2'
import { getSchemaName } from '@/utils/dataClassMap'

/**
 * Util function that map the dataItem to its schema and fetch all the necessary values
 * To be used with Forms where reading/editing the data is needed
 * @param dataItem the data item fetched from the API using `scrivito` `DataClass`
 * @param getValues from `react-hook-form` methods
 * @param watch from `react-hook-form` methods
 * @returns
 */
export const getDataItemFormMap = (
  dataItem: DataItem,
  getValues?: UseFormGetValues<FieldValues>,
  watch?: UseFormWatch<FieldValues>,
): DataItemMap => {
  const dataClassName = dataItem.dataClassName()!
  const schemaName = getSchemaName(dataClassName)
  const { schemaGroups, groupsOrder, schemaAttributes } =
    getSchemaAttributes(schemaName)

  const dataItemMap: DataItemMap = groupsOrder.flatMap((groupName) => {
    const group = schemaGroups[groupName]
    if (!group) return []

    const fieldsMap: FieldMap[] = group.attributeOrder.map((attribute) => {
      const attributeSchema = schemaAttributes[attribute]
      const attributeKey = getCustomFieldKey(attribute, attributeSchema, watch)

      const fieldMap = getFieldMap(
        attributeKey,
        attribute,
        attributeSchema,
        dataItem,
        getValues,
      )

      return fieldMap
    })

    return {
      name: groupName,
      title: group.title,
      fields: fieldsMap,
    }
  })

  return dataItemMap
}

/**
 * Util function that map all `dataItem` attribute values into an Object regardless of the group
 */
export const getDataItemAttributesFlatMap: (
  dataItem: DataItem,
) => DataItemAttributesFlatMap = (dataItem) => {
  const dataItemMap = getDataItemFormMap(dataItem)

  const result = Object.fromEntries(
    dataItemMap.flatMap((group) =>
      group.fields.map((field) => [field.name, field]),
    ),
  )

  return result
}

const getFieldMap = (
  attributeKey: string,
  attribute: string,
  attributeSchema?: SchemaAttribute,
  dataItem?: DataItem,
  getValues?: UseFormGetValues<FieldValues>,
) => {
  const { title, type, enumValues, multiLine } = attributeSchema || {}
  const rawValue = dataItem?.get(attribute)

  const fieldMap: FieldMap = {
    fieldKey: attributeKey,
    name: attribute,
    fieldSchema: attributeSchema,
    title: title,
    value: rawValue,
    displayValue: getAttributeDisplayValue(attributeSchema, rawValue),
    type: type as AttributeType,
    isMultiline: multiLine,
    enumValueOptions: getDataItemEnumSelectArray(
      getValues,
      enumValues,
      dataItem,
    ),
    reference: {
      referenceDataItem: getReferenceDataItem(attributeSchema, rawValue),
      loadReferenceOptions: getReferenceOptionsFunction(attributeSchema),
    },
  }

  return fieldMap
}
